import { useState, useEffect, useRef } from 'react';
import { Fade, IconButton, Snackbar, styled, Tooltip } from '@mui/material';
import { Avatar, Typography, Divider, Box } from '@mui/material';
import { Stack } from '@mui/material';
import { ContentCopyOutlined, CloseOutlined, ContentCopy, FlagOutlined } from '@mui/icons-material';
import { CitationItem, SummaryItemProps } from './Interface';
import { SummaryTypeIconMap } from './Constants';
import { sanitizeNote } from 'op-pages/RO/Notes/sanitizeNote';
import PillCitations from './PillCitations';
import {
  useCopyToClipboard,
  formatSummaryContent,
  deduplicateNoteCitations,
  removeBulletPoints,
} from '../SummaryUtils';
import NotificationSnackbar from 'shared-components/components/NotificationSnackbar';
import { FeedbackModal, useFeedbackHandler } from 'shared-components/components/AiFeedback';
import { logDatadogRumCustomAction } from 'op-utils/datadogRumCustomAction';

const SummaryContent = styled('div')<{ $format: boolean }>`
  display: inline;
  line-height: 24px;
  font-size: 14px;
  white-space: ${({ $format }: { $format: boolean }) => ($format ? 'normal' : 'pre-wrap')};
  color: ${(props) => props.theme.palette.text.primary};
  padding-left: 0px;
  word-break: break-word;
  & > * {
    display: inline !important;
  }
  font,
  ul {
    margin-left: 32px !important;
  }
  ,
  ol {
    margin-left: 8px;
  }
  ,
  li {
    margin-bottom: 8px;
    list-style-type: disc;
  }
  ,
  b {
    color: ${(props) => props.theme.palette.text.primary};
    margin: 0px !important;
    font-weight: 500;
    font-size: 14px;
  }
  ,
  div {
    margin-left: 0px;
  }
`;

const SummaryRowContainer = ({
  citations,
  text,
  onRelatedItemClick,
  patientId,
}: {
  citations: CitationItem[];
  text: string;
  onRelatedItemClick: (item: CitationItem) => void;
  patientId?: string;
}) => {
  const [hovering, setHovering] = useState(false);
  return (
    <div
      style={{
        display: 'block',
        width: '100%',
        padding: '4px 80px 4px 8px', // Increased right padding to accommodate expanded citations
        borderRadius: '4px',
        transition: 'background-color 0.2s ease',
        backgroundColor: hovering ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}>
      <SummaryContent $format={true} dangerouslySetInnerHTML={sanitizeNote(removeBulletPoints(text), true)} />
      <PillCitations
        items={deduplicateNoteCitations(citations)}
        onRelatedItemClick={onRelatedItemClick}
        expanded={hovering}
        patientId={patientId}
      />
    </div>
  );
};

export const isContentEmpty = (contents: SummaryItemProps['contents']): boolean => {
  if (!contents || contents.length === 0) return true;
  return !contents.some((content) => content.text?.trim() || content.subItems?.some((subItem) => subItem.text?.trim()));
};

const SummaryItem = ({
  title,
  summaryType,
  contentType,
  contents,
  onRelatedItemClick,
  onCitationGroupSelection,
  selectedCitationGroupId,
  traceId,
  spanId,
  userRoleData,
  patientId,
}: SummaryItemProps): JSX.Element => {
  const [onHover, setOnHover] = useState(false);
  const icon = SummaryTypeIconMap[summaryType as keyof typeof SummaryTypeIconMap];
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { copySnackbarOpen, copyError, copyToClipboard, setCopySnackbarOpen } = useCopyToClipboard();
  const [copiedButton, setCopiedButton] = useState<null | 'content' | 'trace' | 'span'>(null);
  const clearTimerRef = useRef<number | null>(null);
  const isEmpty = isContentEmpty(contents);

  // Clear timer when component unmounts
  useEffect(() => {
    return () => {
      if (clearTimerRef.current) window.clearTimeout(clearTimerRef.current);
    };
  }, []);
  // AI Feedback hook
  const {
    feedbackModalOpen,
    feedbackSnackbarOpen,
    feedbackLoading,
    feedbackSuccess,
    feedbackErrorMessage,
    openFeedbackModal,
    closeFeedbackModal,
    submitFeedback,
    closeFeedbackSnackbar,
  } = useFeedbackHandler({
    defaultCommentPrefix: `AI Summary feedback for ${title} (${contentType} view)`,
  });

  const handleCopyToClipboard = async (value?: string | number, buttonType?: 'content' | 'trace' | 'span') => {
    logDatadogRumCustomAction('ai_summary_copy', {
      patientId,
      viewMode: contentType,
      key: summaryType as string,
    });
    const textToCopy = value != null ? String(value) : formatSummaryContent(title, contents || []);
    await copyToClipboard(textToCopy);

    setCopiedButton(buttonType ?? 'content');
    if (clearTimerRef.current) window.clearTimeout(clearTimerRef.current);
    clearTimerRef.current = window.setTimeout(() => setCopiedButton(null), 2000);
  };

  const handleSubmitFeedback = async (category: string, comments: string) => {
    logDatadogRumCustomAction('ai_summary_feedback_submit', {
      patientId,
      category,
      viewMode: contentType,
      key: summaryType as string,
      value: comments.length > 0 ? 'has_comments' : 'no_comments',
    });
    // Prepare summary content for feedback
    const summaryContent = {
      summaryType,
      contentType,
      title,
      contents: contents?.map((content) => ({
        text: content.text,
        citations: content.citations?.map((citation) => ({
          title: citation.title,
          documentType: citation.documentType,
          dateOfDocument: citation.dateOfDocument,
        })),
        subItems: content.subItems?.map((subItem) => ({
          text: subItem.text,
          citations: subItem.citations?.map((citation) => ({
            title: citation.title,
            documentType: citation.documentType,
            dateOfDocument: citation.dateOfDocument,
          })),
        })),
      })),
    };

    await submitFeedback(category, comments, summaryContent, traceId, spanId);
  };

  return (
    <Stack
      flexGrow={1}
      direction="row"
      gap={2}
      sx={{ maxWidth: '768px', padding: '32px 0px 0px 0px' }}
      onMouseEnter={() => setOnHover(true)}
      onMouseLeave={() => setOnHover(false)}>
      <Avatar sx={{ backgroundColor: 'primary.main', height: '32px', width: '32px' }}>{icon}</Avatar>
      <Stack flexGrow={1}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', height: '32px' }}>
          {title}
        </Typography>
        {isEmpty ? (
          <ul data-testid={`empty-state-${summaryType}`}>
            <li style={{ listStyleType: 'disc', marginLeft: '16px' }}>
              <Box
                sx={{
                  display: 'block',
                  width: '100%',
                  padding: '4px 80px 4px 8px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s ease',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: '24px', fontSize: '14px' }}>
                  No relevant information found
                </Typography>
              </Box>
            </li>
          </ul>
        ) : (
          <ul>
            {contents?.map((content, index) => (
              <li
                key={index}
                style={{
                  listStyleType: summaryType === 'overview' || !content.subItems?.length ? 'disc' : 'none',
                  marginLeft: summaryType === 'overview' || !content.subItems?.length ? '16px' : '0px',
                }}>
                <SummaryRowContainer
                  citations={content.citations}
                  text={content.text}
                  onRelatedItemClick={onRelatedItemClick}
                  patientId={patientId}
                />
                <ul style={{ paddingLeft: '16px', marginLeft: '16px' }}>
                  {content.subItems?.map((subItem, index) => (
                    <li key={index} style={{ listStyleType: 'disc' }}>
                      <SummaryRowContainer
                        citations={subItem?.citations || []}
                        text={subItem.text}
                        onRelatedItemClick={onRelatedItemClick}
                        patientId={patientId}
                      />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
        {onHover ? (
          <Stack direction="row" gap="8px" height="48px" sx={{ padding: '8px 0px' }}>
            {!isEmpty && (
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  sx={{ width: '28px', height: '28px' }}
                  aria-label="Copy summary content to clipboard"
                  onClick={() => handleCopyToClipboard(undefined, 'content')}>
                  {copiedButton === 'content' ? (
                    <ContentCopy fontSize="small" />
                  ) : (
                    <ContentCopyOutlined fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {!isEmpty && userRoleData?.user?.isSuperuser && traceId && (
              <Tooltip title={`Copy trace ID: ${traceId}`}>
                <IconButton
                  size="small"
                  sx={{ width: '28px', height: '28px' }}
                  aria-label={`Copy trace ID ${traceId} to clipboard`}
                  onClick={() => handleCopyToClipboard(traceId, 'trace')}>
                  {copiedButton === 'trace' ? (
                    <ContentCopy fontSize="small" />
                  ) : (
                    <ContentCopyOutlined fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {!isEmpty && userRoleData?.user?.isSuperuser && spanId && (
              <Tooltip title={`Copy span ID: ${spanId}`}>
                <IconButton
                  size="small"
                  sx={{ width: '28px', height: '28px' }}
                  aria-label={`Copy span ID ${spanId} to clipboard`}
                  onClick={() => handleCopyToClipboard(spanId, 'span')}>
                  {copiedButton === 'span' ? (
                    <ContentCopy fontSize="small" />
                  ) : (
                    <ContentCopyOutlined fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Flag an issue">
              <IconButton
                size="small"
                sx={{ width: '28px', height: '28px' }}
                aria-label="Open AI feedback modal to report an issue"
                onClick={() => {
                  logDatadogRumCustomAction('ai_summary_feedback_open', {
                    patientId,
                    viewMode: contentType,
                    key: summaryType as string,
                  });
                  openFeedbackModal();
                }}>
                <FlagOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : (
          <Box sx={{ height: '48px' }} />
        )}
        <Snackbar
          open={snackbarOpen}
          onClose={() => setSnackbarOpen(false)}
          TransitionComponent={Fade}
          message="Thanks for your feedback"
          key={'Fade'}
          autoHideDuration={2000}
          sx={{ height: '40px', '& .MuiSnackbarContent-root': { padding: '2px 16px' } }}
          action={
            <IconButton size="small" onClick={() => setSnackbarOpen(false)}>
              <CloseOutlined fontSize="small" sx={{ fill: 'white' }} />
            </IconButton>
          }
        />
        <NotificationSnackbar
          open={copySnackbarOpen}
          onClose={() => setCopySnackbarOpen(false)}
          type={copyError ? 'error' : 'copy'}
          autoHideDuration={2000}
        />
        <NotificationSnackbar
          open={feedbackSnackbarOpen}
          onClose={closeFeedbackSnackbar}
          type={feedbackSuccess ? 'feedback' : 'error'}
          message={feedbackSuccess ? undefined : feedbackErrorMessage}
        />
        <FeedbackModal
          open={feedbackModalOpen}
          onClose={closeFeedbackModal}
          onSubmit={handleSubmitFeedback}
          loading={feedbackLoading}
        />
        <Divider />
      </Stack>
    </Stack>
  );
};

export default SummaryItem;
